import React, { useState, useEffect } from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Clock, CreditCard, AlertTriangle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';

const TrialStatus = () => {
  const { t } = useLanguage();
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchSubscriptionStatus();
    
    // Listen for subscription updates (e.g., after payment)
    const handleSubscriptionUpdate = () => {
      fetchSubscriptionStatus();
    };
    
    window.addEventListener('subscriptionUpdated', handleSubscriptionUpdate);
    
    return () => {
      window.removeEventListener('subscriptionUpdated', handleSubscriptionUpdate);
    };
  }, []);

  const fetchSubscriptionStatus = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/subscription/status`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (response.ok) {
        const status = await response.json();
        setSubscriptionStatus(status);
      }
    } catch (error) {
      console.error('Failed to fetch subscription status:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading || !subscriptionStatus) {
    return null;
  }

  // Show paid subscription status
  if (subscriptionStatus.has_active_subscription && !subscriptionStatus.is_trial) {
    const daysRemaining = subscriptionStatus.subscription_days_remaining || 0;
    const isExpiringSoon = subscriptionStatus.is_expiring_soon || daysRemaining < 7;
    const planType = subscriptionStatus.subscription_type || t('activeSubscription');
    const isStarterPlan = planType.toLowerCase().includes('starter');
    const isProfessionalMonthly = planType.toLowerCase().includes('professional') && planType.toLowerCase().includes('monthly');
    
    return (
      <Card className={`mb-4 ${isExpiringSoon ? 'border-orange-200 bg-orange-50' : isStarterPlan ? 'border-blue-200 bg-blue-50' : 'border-green-200 bg-green-50'}`}>
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {isExpiringSoon ? (
                <AlertTriangle className="h-5 w-5 text-orange-600 animate-pulse" />
              ) : (
                <CreditCard className={`h-5 w-5 ${isStarterPlan ? 'text-blue-600' : 'text-green-600'}`} />
              )}
              <div>
                <div className="flex items-center gap-2">
                  <Badge variant={isExpiringSoon ? "destructive" : "default"} className={isExpiringSoon ? '' : isStarterPlan ? 'bg-blue-600' : 'bg-green-600'}>
                    {planType}
                  </Badge>
                  <span className={`font-medium ${isExpiringSoon ? 'text-orange-700' : isStarterPlan ? 'text-blue-700' : 'text-green-700'}`}>
                    {isExpiringSoon ? `⚠️ ${daysRemaining} ${t('daysUntilExpiry')}` : `${t('expiresIn')} ${daysRemaining} ${t('daysRemaining')}`}
                  </span>
                </div>
                <p className={`text-sm ${isExpiringSoon ? 'text-orange-600' : isStarterPlan ? 'text-blue-600' : 'text-green-600'} mt-1`}>
                  {isExpiringSoon 
                    ? t('subscriptionExpiresSoon')
                    : isStarterPlan
                    ? `⚠️ ${t('upgradeForFullAccess')}`
                    : isProfessionalMonthly
                    ? `💡 ${t('upgradeToYearlySave')}`
                    : t('fullAccessFeatures')}
                </p>
              </div>
            </div>
            <div className="flex gap-2">
              {(isStarterPlan || isProfessionalMonthly) && !isExpiringSoon && (
                <Button 
                  size="sm" 
                  onClick={() => navigate('/pricing')}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  <CreditCard className="h-4 w-4 mr-2" />
                  {isStarterPlan ? t('upgradeNow') : t('upgradeToYearly')}
                </Button>
              )}
              {subscriptionStatus.can_renew && (
                <Button 
                  size="sm" 
                  onClick={() => navigate('/pricing')}
                  className={isExpiringSoon ? 'bg-orange-600 hover:bg-orange-700 animate-pulse' : 'bg-green-600 hover:bg-green-700'}
                  variant="default"
                  data-testid="renew-plan-button"
                >
                  <CreditCard className="h-4 w-4 mr-2" />
                  {t('renewNow')}
                </Button>
              )}
              {/* Always offer a way to manage/renew the plan, even when not expiring or renewable yet */}
              {!((isStarterPlan || isProfessionalMonthly) && !isExpiringSoon) && !subscriptionStatus.can_renew && (
                <Button 
                  size="sm" 
                  onClick={() => navigate('/pricing')}
                  variant="outline"
                  className={isStarterPlan ? 'border-blue-600 text-blue-700 hover:bg-blue-100' : 'border-green-600 text-green-700 hover:bg-green-100'}
                  data-testid="manage-plan-button"
                >
                  <CreditCard className="h-4 w-4 mr-2" />
                  {t('managePlan')}
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Show trial status for users on trial
  if (subscriptionStatus.is_trial) {
    const daysRemaining = subscriptionStatus.trial_days_remaining || 0;
    const isExpiringSoon = daysRemaining <= 3;
    
    return (
      <Card className={`mb-4 ${isExpiringSoon ? 'border-orange-200 bg-orange-50' : 'border-blue-200 bg-blue-50'}`}>
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Clock className={`h-5 w-5 ${isExpiringSoon ? 'text-orange-600' : 'text-blue-600'}`} />
              <div>
                <div className="flex items-center gap-2">
                  <Badge variant={isExpiringSoon ? "destructive" : "default"} className={isExpiringSoon ? '' : 'bg-blue-600'}>
                    Free Trial
                  </Badge>
                  <span className={`font-medium ${isExpiringSoon ? 'text-orange-700' : 'text-blue-700'}`}>
                    {daysRemaining} {daysRemaining === 1 ? 'day' : 'days'} remaining
                  </span>
                </div>
                <p className={`text-sm ${isExpiringSoon ? 'text-orange-600' : 'text-blue-600'} mt-1`}>
                  {isExpiringSoon 
                    ? `⚠️ ${t('subscriptionExpiresSoon')}`
                    : t('enjoyFullAccess')}
                </p>
              </div>
            </div>
            <Button 
              size="sm" 
              onClick={() => navigate('/pricing')}
              className={isExpiringSoon ? 'bg-orange-600 hover:bg-orange-700' : 'bg-blue-600 hover:bg-blue-700'}
            >
              <CreditCard className="h-4 w-4 mr-2" />
              {t('viewPlans')}
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Show expired trial / no access
  return (
    <Card className="mb-4 border-red-200 bg-red-50">
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <AlertTriangle className="h-5 w-5 text-red-600" />
            <div>
              <div className="flex items-center gap-2">
                <Badge variant="destructive">Trial Expired</Badge>
              </div>
              <p className="text-sm text-red-600 mt-1">
                Your trial has ended. Subscribe to regain access to Composys MaintPro.
              </p>
            </div>
          </div>
          <Button 
            size="sm" 
            onClick={() => navigate('/pricing')}
            className="bg-red-600 hover:bg-red-700"
          >
            <CreditCard className="h-4 w-4 mr-2" />
            Subscribe Now
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default TrialStatus;